/**
 * Blowing bubbles (spec 02 §3 Blow), ported from rebb64 D_22E8 (fire) and D_2301 (the animation and
 * the bubble). Fire starts a 6-tick timer; each following tick sets a blow frame; on the tick the timer
 * reads 4 (the 3rd after the press) the bubble is made; the tick after it reads 0 the player stands.
 */

import { blowBubble } from '../bubbles';
import { addScore } from '../score';
import {
  BLOW_COOLDOWN, BLOW_COOLDOWN_CANDY, BLOW_FRAMES, BLOW_SPAWN_TICK, BLOW_TICKS, RING_SCORE,
} from './tuning';
import type { Facing, Player, World } from '../types';

/**
 * Main-loop passes completed before this frame's players run. The world runs the players before the
 * pass that falls on the same frame, so that pass is not counted yet.
 */
export const passesDone = (world: World): number => world.clock.mTicks - (world.clock.mTick ? 1 : 0);

/** D_22E8: fire held. Ignored while the last bubble's cooldown runs (D_A824) or while already blowing. */
export function tryBlow(world: World, p: Player): void {
  const m = p.motion;
  if (passesDone(world) < m.blowReadyAt || m.blow >= 0) return;
  m.blow = BLOW_TICKS;
  m.blowSide = (m.frame & 4) >> 1;
  world.events.push({ type: 'blow', player: p.id });
}

/**
 * D_2301: one tick of the blow animation, run first in the player's tick while `blow >= 0`. The bubble
 * comes from the BUBBLE stream's `blowBubble`; with no free slot the animation plays on, and no
 * cooldown starts (L2321 returns before D_A824 is set).
 */
export function blowStep(world: World, p: Player): void {
  const m = p.motion;
  const t = m.blow;
  if (t === 0) {
    m.frame = m.blowSide * 2;
    m.blow = -1;
    return;
  }
  m.frame = m.blowSide + (BLOW_FRAMES[t] ?? 0);
  m.blow = t - 1;
  if (t !== BLOW_SPAWN_TICK || !blowBubble(world, p.id)) return;
  m.blowReadyAt = passesDone(world) + (m.yellowCandy ? BLOW_COOLDOWN_CANDY : BLOW_COOLDOWN);
  if (m.blueRing) addScore(world, p.id, RING_SCORE);
}

/**
 * Where the C64 puts the bubble a player blows on this tick (D_2301 L232A), for the BUBBLE stream's
 * `blowBubble`: at the player's X, 8 px further when facing right (the frame is 9, not $B); 8 px lower
 * when blown during a jump with the body 4 or more px into its tile row ($24 >= 4). Same VIC frame as
 * the player; `facing` is the shot's direction (D_0193: 0 right, $80 left).
 */
export function blowOrigin(p: Player): { x: number; y: number; facing: Facing } {
  const right = p.motion.blowSide === 0;
  const lower = p.motion.jump >= 0 && ((p.y - 0x15) & 7) >= 4;
  return { x: right ? p.x + 8 : p.x, y: lower ? p.y + 8 : p.y, facing: right ? 1 : -1 };
}
